import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { eventApi } from '@/services/api';
import { EventListing } from '@/types';
import { CatalogCard } from '@/components/public/CatalogCard';
import { usePublicSearchStore } from '@/store/publicSearchStore';
import { CategoryCardCarousel, groupItemsByCategory } from '@/components/public/CategoryCardCarousel';

export const EventsPage = () => {
  const [events, setEvents] = useState<EventListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const searchQuery = usePublicSearchStore((state) => state.queries.events);
  const trimmedSearchQuery = searchQuery.trim();
  const deferredSearchQuery = useDeferredValue(trimmedSearchQuery);
  const normalizedSearchQuery = deferredSearchQuery.toLowerCase();
  const visibleEvents = normalizedSearchQuery
    ? events.filter((eventItem) => eventItem.title.toLowerCase().includes(normalizedSearchQuery))
    : events;
  const eventGroups = useMemo(() => groupItemsByCategory(visibleEvents), [visibleEvents]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await eventApi.getAllEvents({ signal: controller.signal });
        setEvents(data);
      } catch (fetchError) {
        if ((fetchError as Error).name !== 'AbortError') {
          setError('Events are not available right now. Please try again soon.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
    return () => controller.abort();
  }, []);

  return (
    <section className="courses-page min-h-full">
      <div className="courses-shell">
        {trimmedSearchQuery ? (
          <p className="courses-search-note">
            Showing results for <span className="text-slate-950">&quot;{searchQuery}&quot;</span>
          </p>
        ) : null}

        {isLoading ? (
          <div className="courses-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <div key={item} className="courses-skeleton" />
            ))}
          </div>
        ) : error ? (
          <div className="courses-empty-state">{error}</div>
        ) : visibleEvents.length === 0 ? (
          <div className="courses-empty-state">
            {trimmedSearchQuery ? 'No events or workshops matched your search.' : 'No events found.'}
          </div>
        ) : (
          <div className="space-y-8">
            {eventGroups.map((group) => (
              <CategoryCardCarousel
                key={group.id}
                title={group.name}
                itemCount={group.items.length}
                itemLabel="event"
              >
                {group.items.map((eventItem) => (
                  <CatalogCard
                    key={eventItem.id}
                    href={`/events/${eventItem.slug}`}
                    imageUrl={eventItem.coverImageUrl}
                    imageAlt={eventItem.title}
                    title={eventItem.title}
                    ctaLabel={eventItem.category === 'workshop' ? 'View Details' : 'Learn More'}
                    isFree={eventItem.isFree}
                    originalPrice={eventItem.priceText}
                    videoUrl={eventItem.previewVideoUrl}
                  />
                ))}
              </CategoryCardCarousel>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
